import React, { useState, useEffect } from "react";
import LawDetailModal from "./LawDetailModal";

interface LawArticle {
  id: string;
  title: string;
  content: string;
  lawCode: string;
}

interface LawSectionProps {
  title: string;
  articles: LawArticle[];
  isLoading: boolean;
  searchQuery: string;
}

const INITIAL_ARTICLES_LIMIT = 3;

function LawSection({
  title,
  articles,
  isLoading,
  searchQuery,
}: LawSectionProps) {
  const [showAll, setShowAll] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selected, setSelected] = useState<LawArticle | null>(null);

  useEffect(() => {
    setShowAll(false);
  }, [searchQuery]);

  if (isLoading) {
    return (
      <div className="space-y-3 sm:space-y-4 md:space-y-5">
        <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 uppercase">
          [{title}]
        </h2>
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 md:p-5 animate-pulse"
          >
            <div className="h-4 sm:h-5 md:h-6 bg-gray-200 rounded w-1/3 mb-2 md:mb-3" />
            <div className="h-3 sm:h-4 bg-gray-200 rounded w-full mb-2" />
            <div className="h-3 sm:h-4 bg-gray-200 rounded w-5/6" />
          </div>
        ))}
      </div>
    );
  }

  if (articles.length === 0 && !searchQuery) {
    return null;
  }

  const displayArticles = showAll
    ? articles
    : articles.slice(0, INITIAL_ARTICLES_LIMIT);
  const hasMoreArticles = articles.length > INITIAL_ARTICLES_LIMIT;
  const shouldShowMoreButton = hasMoreArticles && !showAll;

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-5">
      <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 uppercase">
        [{title}]
      </h2>
      {articles.length === 0 && searchQuery ? (
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 text-center text-gray-500">
          <p className="text-sm sm:text-base">
            Không tìm thấy điều luật nào phù hợp
          </p>
        </div>
      ) : (
        <>
          {displayArticles.map((article, index) => (
            <div
              key={article.id}
              id={`article-${index}`}
              className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 md:p-5 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {
                setSelected(article);
                setOpenModal(true);
              }}
            >
              <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 mb-2 md:mb-3">
                {article.title}
              </h3>
              <p
                className="text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed line-clamp-2"
                dangerouslySetInnerHTML={{
                  __html: article.content.replace(/\n/g, "<br>"),
                }}
              ></p>
            </div>
          ))}
          {shouldShowMoreButton && (
            <button
              onClick={() => setShowAll(true)}
              className="text-[#1677ffx] hover:text-blue-800 active:text-blue-900 text-xs sm:text-sm md:text-base font-medium transition-colors mt-2"
            >
              Xem thêm...
            </button>
          )}
        </>
      )}
      <LawDetailModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        article={
          selected
            ? {
                id: selected.id,
                title: selected.title,
                content: selected.content,
                lawCode: selected.lawCode,
              }
            : undefined
        }
      />
    </div>
  );
}

export default LawSection;
